import { Api } from '@/api';
import { ref } from 'vue';
import { toast } from 'vue-sonner';
import { mergeTagQuillParser } from '@/components/shared/merge_tag/quill_input/mergeTagQuillParser';
import { defaultEmailSubject, defaultEmailBody, defaultSmsMessage } from './defaultContent';
import type { UseScheduleInterviewInputArgs } from './useScheduleInterviewInput';
import { useCurrentUserStore } from '@/stores/currentUser';

export type UseCandidateScheduleInputReturn = ReturnType<typeof useCandidateScheduleInput>;

export type CandidateScheduleView = 'review' | 'edit_email' | 'edit_sms';

export const useCandidateScheduleInput = ({ contact, event_type }: UseScheduleInterviewInputArgs) => {
  const userStore = useCurrentUserStore();
  const postmarkVerified = userStore.currentUser?.postmarkEmailVerified ?? false;

  const view = ref<CandidateScheduleView>('review');
  const shouldSendEmail = ref(postmarkVerified);
  const shouldSendSms = ref(false);
  const emailSubject = ref(defaultEmailSubject());
  const emailBody = ref(defaultEmailBody(event_type.value));
  const smsMessage = ref(defaultSmsMessage(event_type.value));
  const validate = (): boolean => {
    if (!shouldSendEmail.value && !shouldSendSms.value) {
      toast.error('Validation Error', { description: 'Select at least one notification method' });
      return false;
    }
    if (shouldSendEmail.value && (!emailSubject.value || !emailBody.value)) {
      toast.error('Validation Error', { description: 'Email subject and body are required' });
      return false;
    }
    if (shouldSendSms.value && !smsMessage.value) {
      toast.error('Validation Error', { description: 'SMS message is required' });
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (shouldSendEmail.value) {
      const response = await Api.Contacts.sendContactEmail({
        contactId: contact.value.id,
        email: contact.value.email,
        subject: emailSubject.value,
        body: mergeTagQuillParser().serialize(emailBody.value),
      });
      if (response?.status === 'error') {
        toast.error('Email Failed', { description: response.messages.join(', ') });
      }
    }

    if (shouldSendSms.value) {
      const response = await Api.Contacts.sendContactMessage({
        contactId: contact.value.id,
        content: smsMessage.value,
      });
      if (response?.status === 'error') {
        toast.error('SMS Failed', { description: response.messages.join(', ') });
      }
    }
  };

  const canReceiveSms = contact.value.canReceiveSms;

  return {
    view,
    postmarkVerified,
    canReceiveSms,
    shouldSendEmail,
    shouldSendSms,
    emailSubject,
    emailBody,
    smsMessage,
    validate,
    submit,
  };
};
